import { Component, computed, inject, input, output, ViewChild, signal, OnInit, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IconComponent } from '../../../../@core/components/ui/icon/icon.component';
import { ButtonComponent } from '../../../../@core/components/ui/button/button.component';
import { InputComponent } from '../../../../@core/components/ui/input/input.component';
import { DialogComponent } from '../../../../@core/components/ui/dialog/dialog.component';
import { UserCreditEntry, UserCreditsData, UserCreditsService } from '../../services/user-credits.service';
import { UserCreditsStateService } from '../../services/user-credits-state.service';
import { CREDITS_SVG } from 'src/app/common/svgs';
import { SanitizeHtmlPipe } from '../../../../@shared/pipes/sanitizeHtml.pipe';
import { AssetUrlPipe } from '../../../../@shared/pipes/asset-url.pipe';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { RequestAccessService } from 'src/app/contract-management/services/request-access.service';
import { ToastService } from '../../../../@core/services/toast.service';
import { SubscriptionStateService } from '../../../../@core/services/subscription-state.service';
import { AuthenticationService } from '../../../../@core/services/authentication.service';
import { PAYMENT_MODE, CONTRACT_USER_ROLE } from 'src/app/@core/models/model';

const TOTAL_BLOCKS = 40;
const BLOCKS_PER_ROW = 10;

interface ModeConfig {
  key: 'ask' | 'agent';
  title: string;
  subtitle: string;
  iconType: string;
  iconSource: 'lucide' | 'svg';
  iconBg: string;
  iconColor: string;
  textColor: string;
  consumed: number;
  allocated: number;
  consumedPct: number;
  remaining: number;
  resets: number | null;
  expires: string | null;
  severity: { label: string; classes: string };
  brickRows: number[][];
  blockConsumed: string;
  blockRemaining: string;
  exhausted: boolean;
}

@Component({
  selector: 'app-credits-overview',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, ButtonComponent, InputComponent, DialogComponent, Popover, Tooltip, SanitizeHtmlPipe, AssetUrlPipe],
  templateUrl: './credits-overview.component.html',
})
export class CreditsOverviewComponent implements OnInit {
  @ViewChild('creditsPopover') creditsPopover!: Popover;

  credits = input<UserCreditsData | null>(null);
  fetchedCredits = signal<UserCreditsData | null>(null);
  resolvedCredits = computed(() => this.credits() ?? this.creditsState.credits() ?? this.fetchedCredits());

  variant = input<'cards' | 'layout' | 'chip' | 'exhaustioncard' | 'exhaustionlayout'>('layout');
  filter = input<'All' | 'ask' | 'agent'>('All');
  // Credit mode the host feature actually consumes. Drives the exhaustion-card
  // ready/depleted state (and hence the Resume button): a feature running in
  // ask mode must only recover once ASK credits are topped up, not agent.
  // Defaults to 'agent' to preserve the previous agent-only behaviour.
  mode = input<'ask' | 'agent'>('agent');
  needActionButtons = input<boolean>(true);
  needHeader = input<boolean>(true)
  showViewUsage = input<boolean>(true);
  showGetCredits = input<boolean>(true);
  showGetCreditsInChip = input<boolean>(false);
  askModeEnabled = input<boolean | undefined>(undefined);
  agentModeEnabled = input<boolean | undefined>(undefined);
  featureId = input<string>('');
  feature = input<string>('');
  bottomImageSvg = input<string>('');
  // Asset path for a bottom image (rendered as <img> via assetUrl), alternative to bottomImageSvg.
  bottomImagePath = input<string>('');

  userRole = input<string>('');

  label = input<string>('Resume');

  viewUsage = output<void>();
  getCredits = output<{ ask: number; agent: number }>();
  resume = output<{ threadId?: string }>();
  exhaustionState = output<'depleted' | 'ready' | null>();

  private authService = inject(AuthenticationService);

  isAskModeEnabled = computed(() =>
    this.askModeEnabled() ?? (this.authService.getUser()?.agentModeSettings?.askMode ?? true)
  );
  isAgentModeEnabled = computed(() =>
    this.agentModeEnabled() ?? (this.authService.getUser()?.agentModeSettings?.agentMode ?? true)
  );

  canRequestCredits = computed(() => this.isAskModeEnabled() || this.isAgentModeEnabled());


  canResume = computed(() => {
    const role = this.userRole();
    if (!role) return true;
    return role === CONTRACT_USER_ROLE.OWNER
      || role === CONTRACT_USER_ROLE.EDITOR
      || role === CONTRACT_USER_ROLE.ADMIN;
  });

  requestDialogVisible = signal<boolean>(false);
  isResuming = signal<boolean>(false);
  askRequest = signal<number | null>(null);
  agentRequest = signal<number | null>(null);


  askRemaining = computed(() => this.resolvedCredits()?.ask?.remaining ?? 0);
  agentRemaining = computed(() => this.resolvedCredits()?.agent?.remaining ?? 0);

  askExhausted = computed(() => {
    const ask = this.resolvedCredits()?.ask;
    return !!ask && ask.remaining <= 0;
  });
  agentExhausted = computed(() => {
    const agent = this.resolvedCredits()?.agent;
    return !!agent && agent.remaining <= 0;
  });
  bothExhausted = computed(() => this.askExhausted() && this.agentExhausted());
  chipTooltip = computed<string | undefined>(() => {
    if (this.bothExhausted()) return 'All credits exhausted';
    if (this.askExhausted()) return 'Ask credits exhausted';
    if (this.agentExhausted()) return 'Agent credits exhausted';
    return undefined;
  });

  hasExhaustion = computed(() => {
    const exhaustions = this.resolvedCredits()?.exhaustions ?? [];
    const fId = this.featureId();
    const f = this.feature();
    if (!fId && !f) return false;
    return exhaustions.some(e => e.featureId === fId && e.feature === f);
  });

  matchedExhaustion = computed(() => {
    const fId = this.featureId();
    const f = this.feature();
    return (this.resolvedCredits()?.exhaustions ?? []).find(e => e.featureId === fId && e.feature === f);
  });

  creditsAvailable = computed(() => {
    const credits = this.resolvedCredits();
    return (credits?.[this.mode()]?.remaining ?? 0) > 0;
  });

  /** Host-supplied exhaustion, OR'd with the `exhaustions` row lookup. For surfaces whose item has no
   *  row of its own: an agent downstream of the one that ran out is never enqueued, so nothing ever
   *  records it. 'ready' + Resume still require a real row — there is no thread to resume without one. */
  forceExhausted = input<boolean>(false);

  exhaustionCardState = computed<'depleted' | 'ready' | null>(() => {
    if (!this.creditsAvailable()) return (this.hasExhaustion() || this.forceExhausted()) ? 'depleted' : null;
    return this.hasExhaustion() ? 'ready' : null;
  });

  CREDITS_SVG = CREDITS_SVG
  readonly Math = Math;

  private subscriptionState = inject(SubscriptionStateService);
  private creditsState = inject(UserCreditsStateService);

  isTrial = computed(() => !this.subscriptionState.isTrial());


  modes = computed<ModeConfig[]>(() => {
    const f = this.filter();
    const all: ModeConfig[] = [
      {
        key: 'ask',
        title: 'Ask mode',
        subtitle: 'Simple questions & tasks',
        iconType: 'zap',
        iconSource: 'lucide',
        iconBg: 'bg-gray-100',
        iconColor: this.askExhausted() ? 'text-red-500' : '',
        textColor: 'text-arena-orange',
        consumed: this.resolvedCredits()?.ask?.consumed ?? 0,
        allocated: this.resolvedCredits()?.ask?.allocated ?? 0,
        consumedPct: this.creditsState.consumedPct(this.resolvedCredits()?.ask ?? null) ?? 0,
        remaining: this.resolvedCredits()?.ask?.remaining ?? 0,
        resets: this.resolvedCredits()?.ask?.resets ?? null,
        expires: this.resolvedCredits()?.ask?.expires ?? null,
        severity: this.getSeverity(this.resolvedCredits()?.ask ?? null),
        brickRows: this.buildBrickRows(this.resolvedCredits()?.ask ?? null),
        blockConsumed: 'bg-arena-orange',
        blockRemaining: 'bg-arena-orange-100',
        exhausted: this.askExhausted(),
      },
      {
        key: 'agent',
        title: 'Agentic mode',
        subtitle: 'Complex reasoning & tasks',
        iconType: CREDITS_SVG.AGENT_MODE,
        iconSource: 'svg',
        iconBg: 'bg-arena-blue-100',
        iconColor: this.agentExhausted() ? 'text-red-500 icon-force-color' : 'text-arena-blue',
        textColor: 'text-arena-blue',
        consumed: this.resolvedCredits()?.agent?.consumed ?? 0,
        allocated: this.resolvedCredits()?.agent?.allocated ?? 0,
        consumedPct: this.creditsState.consumedPct(this.resolvedCredits()?.agent ?? null) ?? 0,
        remaining: this.resolvedCredits()?.agent?.remaining ?? 0,
        resets: this.resolvedCredits()?.agent?.resets ?? null,
        expires: this.resolvedCredits()?.agent?.expires ?? null,
        severity: this.getSeverity(this.resolvedCredits()?.agent ?? null),
        brickRows: this.buildBrickRows(this.resolvedCredits()?.agent ?? null),
        blockConsumed: 'bg-arena-blue',
        blockRemaining: 'bg-arena-blue-100',
        exhausted: this.agentExhausted(),
      },
    ];
    if (f === 'ask') return all.filter(m => m.key === 'ask');
    if (f === 'agent') return all.filter(m => m.key === 'agent');
    return all;
  });


  constructor(
    private router: Router,
    private userCreditsService: UserCreditsService,
    private requestAccessService: RequestAccessService,
    private toastService: ToastService
  ) {
    effect(() => this.exhaustionState.emit(this.exhaustionCardState()));
  }

  navigateToUsage(): void {
    this.router.navigate(['/settings/credit-usage']);
  }

  ngOnInit(): void {
    if (!this.credits()) {
      this.creditsState.fetchCredits().subscribe({
        error: (err) => console.error('Failed to load user credits', err)
      });
    }
  }

  // When true, the Resume button does NOT call the thread-resume API. Instead it
  // simply emits (resume) and lets the parent perform the recovery action itself.
  // Used by the compare KPI flow, where credits were exhausted at the pre-flight
  // check so no Python thread ever started — the recovery is a fresh retry, not a
  // thread resume.
  externalResume = input<boolean>(false);

  onResumeClick(): void {
    // External-resume mode: hand control to the parent (e.g. compare KPI retry).
    if (this.externalResume()) {
      this.resume.emit({});
      return;
    }

    const exhaustion = this.matchedExhaustion();
    if (!exhaustion?.threadId) return;

    this.isResuming.set(true);
    this.userCreditsService.resumeThread(exhaustion.threadId, exhaustion.feature, exhaustion.featureId, exhaustion.agentName).subscribe({
      next: () => {
        this.resume.emit({ threadId: exhaustion.threadId });
        this.creditsState.refreshCredits().subscribe();
        this.isResuming.set(false);
      },
      error: (err) => {
        console.error('Failed to resume thread', err);
        this.isResuming.set(false);
      },
    });
  }

  openRequestDialog(): void {
    this.creditsPopover?.hide();
    this.askRequest.set(null);
    this.agentRequest.set(null);
    this.requestDialogVisible.set(true);
  }

  submitRequest(): void {
    const ask = this.askRequest() ?? 0;
    const agent = this.agentRequest() ?? 0;
    this.requestAccessService.requestCredits(ask, agent).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success('Request Sent Successfully');
          this.getCredits.emit({ ask, agent });
          this.requestDialogVisible.set(false);
        } else {
          const errorMessage = res.message;
          this.toastService.error(errorMessage);
        }
      },
    });
  }


  private getSeverity(entry: UserCreditEntry | null): { label: string; classes: string } {
    if (!entry || entry.allocated === 0) return { label: 'N/A', classes: 'bg-gray-50 text-gray-400' };
    const remainingPct = entry.remaining / entry.allocated;
    if (remainingPct > 0.5) return { label: 'High', classes: 'bg-emerald-50 text-emerald-600' };
    if (remainingPct > 0.25) return { label: 'Medium', classes: 'bg-arena-blue-100 text-arena-blue' };
    return { label: 'Low', classes: 'bg-arena-orange-100 text-arena-orange' };
  }

  private buildBlocks(entry: UserCreditEntry | null): number[] {
    if (!entry || entry.allocated === 0) return Array(TOTAL_BLOCKS).fill(0);
    const exactCount = (entry.consumed / entry.allocated) * TOTAL_BLOCKS;
    const fullBlocks = Math.floor(exactCount);
    const partial = exactCount - fullBlocks;
    return Array(TOTAL_BLOCKS).fill(0).map((_, i) => {
      if (i < fullBlocks) return 1;
      if (i === fullBlocks && partial > 0) return partial;
      return 0;
    });
  }

  private buildBrickRows(entry: UserCreditEntry | null): number[][] {
    const blocks = this.buildBlocks(entry);
    const rows: number[][] = [];
    for (let i = 0; i < blocks.length; i += BLOCKS_PER_ROW) {
      rows.push(blocks.slice(i, i + BLOCKS_PER_ROW));
    }
    return rows;
  }
}
